<script setup lang="ts">
  import { computed } from 'vue';
  import whiteLogo from '@tundr/theme-assets/images/tundr-logo-white.svg?url';
  import blackLogo from '@tundr/theme-assets/images/tundr-logo-black.svg?url';
  import sidebarLogo from '@tundr/theme-assets/images/tundr-logo-sidebar.svg?url';
  import sidebarLogoBlack from '@tundr/theme-assets/images/tundr-logo-sidebar-black.svg?url';
  import type { TundrLogoProps } from './TundrLogo.types';
  import { TundrLogoVariant } from './TundrLogo.types';
  import { logoClass, logoWidthClass, clickableClass } from './TundrLogo.css';

  const BRAND_NAME = 'Tundr';

  const props = withDefaults(defineProps<TundrLogoProps>(), {
    variant: TundrLogoVariant.Light,
    clickable: false,
    alt: BRAND_NAME,
  });

  defineOptions({
    inheritAttrs: false,
  });

  const logoSrc = computed(() => {
    switch (props.variant) {
      case TundrLogoVariant.Dark:
        return blackLogo;
      case TundrLogoVariant.Small:
        return sidebarLogo;
      case TundrLogoVariant.SmallDark:
        return sidebarLogoBlack;
      default:
        return whiteLogo;
    }
  });

  const isDecorative = computed(() => !props.clickable && props.alt === '');
  const resolvedAlt = computed(() =>
    props.clickable && props.alt === '' ? BRAND_NAME : props.alt,
  );

  const computedClass = computed(() => [
    logoClass,
    logoWidthClass[props.variant],
    { [clickableClass]: props.clickable },
  ]);

  const onKeydown = (event: KeyboardEvent) => {
    if (!props.clickable) return;
    if (event.key !== 'Enter' && event.key !== ' ') return;
    event.preventDefault();
    (event.currentTarget as HTMLElement).click();
  };
</script>

<template>
  <img
    :id="id"
    :data-testid="testId"
    :class="computedClass"
    :src="logoSrc"
    :alt="isDecorative ? '' : resolvedAlt"
    :aria-hidden="isDecorative ? 'true' : undefined"
    :role="clickable ? 'button' : undefined"
    :tabindex="clickable ? 0 : undefined"
    decoding="async"
    v-bind="$attrs"
    @keydown="onKeydown"
  >
</template>
